import { BoxProps } from '@chakra-ui/react';
import { generalUtils } from '@shared/common';
import { useTranslation } from 'next-i18next';
import { ForwardedRef, forwardRef } from 'react';

import { useCheckout } from '~/hooks/use-checkout';
import { SINGLE_PRODUCT_COUPONS, useAppParams } from '~/hooks/useAppParams';

import { useAppRouter } from '../hooks/useAppRouter';

import { FamilyPlanOrderItem } from './FamilyPlanOrderItem';
import { familyPlansIds } from './FamilyPlanUpgradeBox';
import { TransText } from './TransText';

export const OrderSummarySimple = forwardRef((props: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { coupon } = useAppParams();
  const { t } = useTranslation();
  const { funnelName } = useAppRouter();
  const {
    selectedPlan,
    initialPrice,
    finalPrice,
    initialPriceAfterTax,
    currency,
    tax,
    serviceFee,
    initialPriceForDiscount,
    taxName,
  } = useCheckout();
  const is7DaysTrial = selectedPlan?.trial?.period_days === 7;
  const isDiscounted = initialPrice !== finalPrice;
  const couponDiscountPrecents = coupon && coupon.match(/\d{2}/)?.[0] ? Number(coupon.match(/\d{2}/)?.[0] || 20) : 0;
  const couponDiscountAmount = couponDiscountPrecents
    ? Math.abs(initialPriceAfterTax - initialPriceAfterTax / (1 - couponDiscountPrecents / 100))
    : 0;

  const totalDiscountAmount = initialPriceForDiscount - finalPrice - couponDiscountAmount;
  const isFamilyPlanSelected = familyPlansIds.includes(selectedPlan?.id || '');

  return (
    <div ref={ref} className='bg-white rounded-lg border border-neutral-200 p-6'>
      <div className={`flex justify-between mb-2 ${is7DaysTrial ? 'uppercase' : ''}`}>
        <span className='text-sm font-medium text-dark'>
          {`${t(is7DaysTrial ? selectedPlan?.title : 'common:x_months_plan', { count: selectedPlan?.plan.interval, interval: is7DaysTrial ? 7 : selectedPlan?.plan.interval })}`}
        </span>
        <span className={`text-sm text-dark ${isDiscounted ? 'line-through' : ''}`}>
          {generalUtils.formatPrice(isDiscounted ? initialPrice : finalPrice, { currency })}
        </span>
      </div>

      {isFamilyPlanSelected ? <FamilyPlanOrderItem /> : null}

      {tax && taxName ? (
        <div className='flex justify-between mt-3 text-dark'>
          <span className='text-sm'>{taxName}</span>
          <span className='text-sm'>{generalUtils.formatPrice(tax, currency)}</span>
        </div>
      ) : null}

      {isDiscounted ? (
        <>
          <div className='flex justify-between mt-3'>
            <span className='text-sm text-red-500'>
              {coupon && SINGLE_PRODUCT_COUPONS.includes(coupon)
                ? 'Coupon discount'
                : funnelName === 'cortisol-bf'
                  ? t('plans:campaigns:black_friday_discount')
                  : selectedPlan?.id === '130'
                    ? 'Exclusive Black Friday Offer Discount'
                    : funnelName === 'cortisol-ny'
                      ? t('plans:campaigns.new_year_discount')
                      : funnelName === 'cortisol-xmas'
                        ? t('plans:campaigns.christmas_discount')
                        : t('common:discount')}{' '}
              (-{((totalDiscountAmount / initialPriceForDiscount) * 100).toFixed(0)}%)
            </span>
            <span className='text-sm text-red-500'>-{generalUtils.formatPrice(totalDiscountAmount, currency)}</span>
          </div>
          {coupon && couponDiscountAmount ? (
            <div className='flex justify-between mt-3'>
              <span className='text-sm text-red-500'>Coupon discount (-{couponDiscountPrecents}%)</span>
              <span className='text-sm text-red-500'>-{generalUtils.formatPrice(couponDiscountAmount, currency)}</span>
            </div>
          ) : null}
        </>
      ) : null}

      {serviceFee ? (
        <div className='flex justify-between mt-3 text-dark'>
          <span className='text-sm'>Service fee</span>
          <span className='text-sm'>{generalUtils.formatPrice(serviceFee, currency)}</span>
        </div>
      ) : null}

      <div className='flex justify-between mt-4 pt-4 border-t border-[#E1E1E1]'>
        <span className='font-bold text-lg text-dark'>
          <TransText transKey='common:total' fontSize='18px' fontWeight={700} />
        </span>
        <span className='font-bold text-lg text-dark'>{generalUtils.formatPrice(finalPrice, currency)}</span>
      </div>

      {coupon ? (
        <div className='flex justify-between mt-3 text-dark'>
          <span className='text-[13px] text-black/60'>Coupon applied</span>
          <span className='font-light leading-none px-3 py-1 bg-black/10 rounded-full text-[13px] uppercase text-dark'>
            {coupon}
          </span>
        </div>
      ) : null}
    </div>
  );
});

OrderSummarySimple.displayName = 'OrderSummarySimple';
